import { useEffect, useState } from 'react';
import { Download, FileText, Loader2, X } from 'lucide-react';
import { api } from '@/api/client';

/**
 * Generate the PVDS for each destination post office, or all of them merged.
 *
 * Destinations come from the mailing's routes rather than an array embedded on
 * the mailing row: post offices are their own table now, so there is one place
 * an address can be wrong and one place to fix it.
 */
export default function PostOfficePVDSModal({ mailing, onClose, onOpenPDF }) {
  const [postOffices, setPostOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [generated, setGenerated] = useState({});
  const [generatingAll, setGeneratingAll] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    api.mailings
      .routes(mailing.id)
      .then((routes) => {
        if (!active) return;
        const seen = new Map();
        for (const route of routes) {
          const office = route.post_office;
          if (office && !seen.has(office.id)) seen.set(office.id, office);
        }
        setPostOffices([...seen.values()]);
      })
      .catch((err) => active && setError(err.message))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [mailing.id]);

  const handleGenerate = async (office) => {
    setGenerating((state) => ({ ...state, [office.id]: true }));
    setError('');

    try {
      const result = await api.documents.generate(mailing.id, 'pvds', {
        post_office_id: office.id,
      });
      setGenerated((state) => ({ ...state, [office.id]: result }));
    } catch (err) {
      setError(err.message || 'Could not generate the PVDS.');
      setTimeout(() => setError(''), 4000);
    } finally {
      setGenerating((state) => ({ ...state, [office.id]: false }));
    }
  };

  const handleGenerateAll = async () => {
    setGeneratingAll(true);
    setError('');

    try {
      const result = await api.documents.generate(mailing.id, 'pvds-all');
      onOpenPDF?.({
        url: result.url,
        title: result.filename || `All PVDS — ${mailing.order_number}`,
        type: 'all_pvds',
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Could not generate the combined PVDS.');
      setTimeout(() => setError(''), 4000);
    } finally {
      setGeneratingAll(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="card modal-card">
        <div className="modal-header">
          <div>
            <h3>PVDS Forms</h3>
            <p>{mailing.mailing_name}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close">
            <X size={22} />
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {loading ? (
          <div className="page-spinner">
            <Loader2 size={24} className="spin" />
          </div>
        ) : postOffices.length === 0 ? (
          <p className="muted">
            This mailing has no destination post offices assigned yet.
          </p>
        ) : (
          <>
            <button
              className="btn btn-primary full-width"
              onClick={handleGenerateAll}
              disabled={generatingAll}
            >
              {generatingAll ? <Loader2 size={18} className="spin" /> : <FileText size={18} />}
              {generatingAll ? 'Generating…' : `Generate all ${postOffices.length} as one PDF`}
            </button>

            <ul className="plain-list">
              {postOffices.map((office) => {
                const ready = generated[office.id];
                return (
                  <li key={office.id} className="list-row">
                    <div>
                      <div className="list-row-title">{office.name}</div>
                      <div className="list-row-sub">
                        {[office.address_line1, office.city, office.state, office.zip]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    </div>

                    {ready ? (
                      <button
                        className="btn btn-outline"
                        onClick={() =>
                          onOpenPDF?.({ url: ready.url, title: office.name, type: 'pvds' })
                        }
                      >
                        <Download size={16} />
                        View
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline"
                        onClick={() => handleGenerate(office)}
                        disabled={generating[office.id]}
                      >
                        {generating[office.id] ? (
                          <Loader2 size={16} className="spin" />
                        ) : (
                          <FileText size={16} />
                        )}
                        Generate
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
